import { useEffect, useRef, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import Modal from "@/components/modal";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import AuditLogChangesTable from "@/components/auditLog/AuditLogChangesTable";
import { apiRequest } from "@/helper/api";
import { replaceDate } from "@/helper/dateHandling";
import {
  createAuditDiffClient,
  type AuditDiffClient,
} from "@/components/auditLog/auditDiffClient";
import type {
  AuditField,
  AuditLogDetail,
  AuditLogSummary,
  AuditTextPage,
} from "@/components/auditLog/auditLogTypes";
import AuditLogTextPage from "@/components/auditLog/AuditLogTextPage";
import { useAuditReferences } from "@/components/auditLog/useAuditReferences";
import styles from "@/components/auditLog/auditLog.module.css";

interface Props {
  entry: AuditLogSummary | null;
  onClose: () => void;
}

export default function AuditLogDetailDialog({ entry, onClose }: Props) {
  const { t } = useTranslation();
  const client = useRef<AuditDiffClient | null>(null);
  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [fields, setFields] = useState<AuditField[]>([]);
  const [original, setOriginal] = useState<AuditTextPage | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const entryId = entry?.id;
  const references = useAuditReferences(fields);

  useEffect(() => {
    const controller = new AbortController();
    let currentClient: AuditDiffClient | null = null;
    setError(false);
    setFields([]);
    setOriginal(null);
    setShowOriginal(false);
    if (entryId === undefined) return;
    setLoading(true);
    const load = async () => {
      try {
        const response = await apiRequest<AuditLogDetail>({
          entity: "auditlog",
          entityId: entryId,
          method: "get",
          config: { signal: controller.signal },
          disableGlobalSpinner: true,
          skipGatewayRetry: true,
        });
        if (controller.signal.aborted) return;
        currentClient = createAuditDiffClient();
        client.current = currentClient;
        const result = await currentClient.request({
          type: "load",
          entry: response.data,
        });
        if (!controller.signal.aborted && result.type === "loaded")
          setFields(result.fields);
      } catch {
        if (!controller.signal.aborted) setError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    void load();
    return () => {
      controller.abort();
      currentClient?.dispose();
      if (client.current === currentClient) client.current = null;
    };
  }, [entryId, retry]);

  const getOriginalPage = async (offset: number) => {
    const current = client.current;
    if (!current) throw new Error("Audit detail is closed");
    const result = await current.request({ type: "page", side: "raw", offset });
    if (client.current !== current || result.type !== "page") return;
    setOriginal(result.page);
  };
  const copyOriginal = async () => {
    const current = client.current;
    if (!current) throw new Error("Audit detail is closed");
    const result = await current.request({ type: "copy", side: "raw" });
    if (client.current === current && result.type === "copy")
      await navigator.clipboard.writeText(result.text);
  };
  const openOriginal = async () => {
    const current = client.current;
    setShowOriginal(true);
    setOriginal(null);
    try {
      await getOriginalPage(0);
    } catch {
      if (client.current === current) setError(true);
    }
  };
  return (
    <Modal
      open={entry !== null}
      title={t("audit_diff_details")}
      onClose={onClose}
      hideSubmitButton
      cancelButtonText={t("close")}
      maxWidth="1100px"
      contentClassName={styles.dialogContent}
    >
      {entry && (
        <>
          <div className={styles.metadata}>
            <strong>
              {t(`audit_action_${entry.action}`)} ·{" "}
              {replaceDate(entry.timestamp, true, true)}
            </strong>
            <span>
              {entry.object_repr_preview}
              {entry.object_repr_truncated && "…"}
            </span>
            <span>
              {t("audit_diff_object_id")}: {entry.object_pk}
            </span>
            <span>
              {t("audit_diff_actor")}:{" "}
              {entry.actor.email ?? t("audit_diff_no_actor")}
            </span>
          </div>
          {loading && (
            <div role="status" className={styles.loading}>
              <CircularProgress size={24} />
              {t("audit_diff_loading")}
            </div>
          )}
          {error && (
            <Alert
              severity="error"
              action={
                <GenericButton
                  size="small"
                  variant="outlined"
                  onClick={() => setRetry((value) => value + 1)}
                >
                  {t("audit_diff_retry")}
                </GenericButton>
              }
            >
              {t("audit_diff_load_failed")}
            </Alert>
          )}
          {!loading && !error && (
            <>
              <div className={styles.detailToolbar}>
                <GenericButton
                  variant={showOriginal ? "outlined" : "contained"}
                  onClick={() => setShowOriginal(false)}
                >
                  {t("audit_diff_comparison")}
                </GenericButton>
                <GenericButton
                  variant={showOriginal ? "contained" : "outlined"}
                  onClick={openOriginal}
                >
                  {t("audit_diff_original")}
                </GenericButton>
              </div>
              {showOriginal ? (
                original ? (
                  <AuditLogTextPage
                    key={`raw-${entry.id}`}
                    side="raw"
                    page={original}
                    onPage={getOriginalPage}
                    onCopy={copyOriginal}
                    neutral
                  />
                ) : (
                  <span role="status">{t("audit_diff_loading")}</span>
                )
              ) : (
                <>
                  {references.hasReferences && (
                    <div className={styles.referenceNotice}>
                      <span className={styles.hint}>
                        {t("audit_diff_reference_hint")}
                      </span>
                      {references.loading ? (
                        <span className={styles.hint} role="status">
                          {t("audit_diff_reference_loading")}
                        </span>
                      ) : references.hasUnresolved ? (
                        <GenericButton
                          size="small"
                          variant="outlined"
                          onClick={references.retry}
                        >
                          {t("audit_diff_reference_retry")}
                        </GenericButton>
                      ) : null}
                    </div>
                  )}
                  {fields.length === 0 && <p>{t("audit_diff_no_changes")}</p>}
                  {client.current && fields.length > 0 && (
                    <AuditLogChangesTable
                      key={`${entry.id}-${retry}`}
                      fields={fields}
                      client={client.current}
                      references={references.labels}
                    />
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
}
